import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';

export default function ActivateHotelTab({ hotel, facilities, roomTypes, floors, rooms, token, onActivated }) {
  const [activating, setActivating] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  // Checklist conditions
  const checks = [
    {
      label: 'Hotel info exists',
      valid: !!hotel,
    },
    {
      label: 'At least one facility exists',
      valid: facilities && facilities.length > 0,
    },
    {
      label: 'At least one room type exists',
      valid: roomTypes && roomTypes.length > 0,
    },
    {
      label: 'At least one floor exists',
      valid: floors && floors.length > 0,
    },
    {
      label: 'Total rooms created equals hotel total_rooms',
      valid:
        hotel && rooms &&
        rooms.length === hotel.total_rooms &&
        hotel.total_rooms > 0,
    },
  ];

  const allValid = checks.every(c => c.valid);
  const alreadyActive = hotel && hotel.status === 'active';

  const handleActivate = async () => {
    setActivating(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(
        '/api/manager/website/hotel/activate',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Hotel activated successfully!');
      if (onActivated) onActivated();
    } catch (err) {
      setError(
        err.response?.data?.message || 'Failed to activate hotel. Please try again.'
      );
    } finally {
      setActivating(false);
      setShowConfirm(false);
    }
  };

  return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: 32 }}>
      <div style={{
        background: '#fffbe6',
        border: '1px solid #ffe58f',
        color: '#ad6800',
        borderRadius: 8,
        padding: 16,
        marginBottom: 24,
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <AlertTriangle size={22} style={{ flexShrink: 0 }} />
        <span>
          Activating the hotel will make it <b>live</b> and available for bookings. Please ensure all required information is complete before proceeding.
        </span>
      </div>
      <ul style={{ listStyle: 'none', padding: 0, marginBottom: 24 }}>
        {checks.map((c, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
            {c.valid ? (
              <CheckCircle color="#22c55e" size={20} style={{ marginRight: 8 }} />
            ) : (
              <XCircle color="#ef4444" size={20} style={{ marginRight: 8 }} />
            )}
            <span style={{ fontWeight: 500 }}>{c.label}</span>
          </li>
        ))}
      </ul>
      {alreadyActive && (
        <div style={{ color: '#22c55e', fontWeight: 600, marginBottom: 16 }}>
          Hotel is already active
        </div>
      )}
      {success && <div style={{ color: '#22c55e', marginBottom: 12 }}>{success}</div>}
      {error && <div style={{ color: '#ef4444', marginBottom: 12 }}>{error}</div>}
      <button
        style={{
          background: alreadyActive ? '#d1fae5' : allValid ? '#2563eb' : '#e5e7eb',
          color: alreadyActive ? '#065f46' : allValid ? '#fff' : '#888',
          fontWeight: 700,
          fontSize: 17,
          border: 'none',
          borderRadius: 6,
          padding: '10px 32px',
          cursor: alreadyActive || !allValid ? 'not-allowed' : 'pointer',
          marginTop: 8,
          boxShadow: allValid && !alreadyActive ? '0 2px 8px rgba(37,99,235,0.13)' : 'none',
          transition: 'background 0.2s, color 0.2s',
        }}
        disabled={alreadyActive || !allValid || activating}
        onClick={() => setShowConfirm(true)}
      >
        Activate Hotel
      </button>
      {showConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 32, minWidth: 320, boxShadow: '0 4px 32px rgba(0,0,0,0.13)' }}>
            <h3 style={{ fontSize: 22, fontWeight: 700, marginBottom: 16 }}>Confirm Activation</h3>
            <p style={{ marginBottom: 24 }}>Are you sure you want to activate the hotel? This action will make the hotel live and available for bookings.</p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
              <button
                style={{ padding: '7px 22px', borderRadius: 5, border: 'none', background: '#e5e7eb', color: '#222', fontWeight: 600 }}
                onClick={() => setShowConfirm(false)}
                disabled={activating}
              >
                Cancel
              </button>
              <button
                style={{ padding: '7px 22px', borderRadius: 5, border: 'none', background: '#2563eb', color: '#fff', fontWeight: 700 }}
                onClick={handleActivate}
                disabled={activating}
              >
                {activating ? 'Activating...' : 'Yes, Activate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
